import { RouteComponentProps } from "react-router-dom"
import { useProject } from "../../../projects/hooks/use-project"
import { useCalendarEventMutations } from "../../../calendar-events/hooks/use-calendar-event-mutations"
import { NewCalendarEventData, useGetCalendarEventsQuery } from "../../../calendar-events/api"
import { NewTaskData, useGetTasksQuery } from "../../../tasks/api"
import { useTaskMutations } from "../../../tasks/hooks/use-task-mutations"
import { useTodoMutations } from "../../../todos/hooks/use-todo-mutations"
import { NewTodoData, useGetTodosQuery } from "../../../todos/api"
import React from "react"
import CircularProgress from "@material-ui/core/CircularProgress"
import { useGetOrganisationByIdQuery } from "../../../organisations/api"
import Calendar from "../../../calendar-events/components/Calendar"
import { adaptCalendarEvent, adaptRecurringEvent, adaptTask, adaptTodo } from "../../../calendar-events/utils"
import { keyBy, merge } from "lodash"
import ErrorList from "../../../components/ErrorList"
import { useAppContext } from "../../../app-context/use-app-context"
import { useTranslation } from "react-i18next"
import { useProjectCalendarSettings } from "../../providers/project-calendar-settings"

type RouteParams = { projectId: string }

type QueryErrors = {
  calendarEvent?: string
  task?: string
  todo?: string
  project?: string
  organisation?: string
  holidays?: string
}

const queryOptions = { refetchOnMountOrArgChange: true } as const

const useLoader = ({ projectId }: { projectId: string }) => {
  const translations = useTranslations()
  const { appContext } = useAppContext()
  const { mainContext, subContext } = appContext
  const context = subContext || mainContext
  const queryErrors = {} as QueryErrors
  const queryParams = { project: projectId }

  const { project, isLoading: isLoadingProject, isError: isErrorProject } = useProject(projectId, queryOptions)

  const {
    data: calendarEvents = [],
    isLoading: isLoadingCalendarEvents,
    isError: isErrorCalendarEvents,
  } = useGetCalendarEventsQuery(queryParams, queryOptions)

  const {
    data: tasks = [],
    isLoading: isLoadingTasks,
    isError: isErrorTasks,
  } = useGetTasksQuery({ ...queryParams, archived: "false" }, queryOptions)

  const {
    data: todos = [],
    isLoading: isLoadingTodos,
    isError: isErrorTodos,
  } = useGetTodosQuery(queryParams, queryOptions)

  const organisationId = project && project?.isOrgProject ? project.maintainerId : ""
  const skipFetchingOrganisation = !organisationId

  const {
    data: organisation,
    isLoading: isLoadingOrganisation,
    isError: isErrorOrganisation,
  } = useGetOrganisationByIdQuery(organisationId, {
    ...queryOptions,
    skip: skipFetchingOrganisation,
  })

  const isLoading =
    isLoadingProject || isLoadingCalendarEvents || isLoadingTasks || isLoadingTodos || isLoadingOrganisation

  const isError = isErrorProject || isErrorCalendarEvents || isErrorTasks || isErrorTodos || isErrorOrganisation

  if (isErrorProject) queryErrors.project = translations.fetchErrorProject
  if (isErrorCalendarEvents) queryErrors.calendarEvent = translations.fetchErrorCalendarEvents
  if (isErrorTasks) queryErrors.task = translations.fetchErrorTasks
  if (isErrorTodos) queryErrors.todo = translations.fetchErrorTodos
  if (isErrorOrganisation) queryErrors.organisation = translations.fetchErrorOrganisation

  return {
    project,
    calendarEvents,
    tasks,
    todos,
    organisation,
    isLoading,
    isError,
    queryErrors,
    context,
  }
}

const ProjectCalendarRoute = ({ match }: RouteComponentProps<RouteParams>) => {
  const translations = useTranslations()
  const { projectId } = match.params
  const { calendarSettings, settingsKey } = useProjectCalendarSettings()
  const { project, calendarEvents, tasks, todos, isLoading, isError, queryErrors, context } = useLoader({ projectId })
  const { createCalendarEvent, updateInfo: updateCalendarEvent } = useCalendarEventMutations()
  const { createOrgTask, createUserTask, updatePlan: updateTask } = useTaskMutations()
  const { createTodo, updateInfo: updateTodo } = useTodoMutations()

  const { events, recurringEvents, entitiesById } = React.useMemo(() => {
    const taskEvents = tasks.filter((t) => t.plannedEndDate || t.plannedStartDate).map(adaptTask)
    const todoEvents = todos.filter((todo) => todo.dueDate).map(adaptTodo)
    const adaptedCalenderEvents = calendarEvents.filter((e) => !e.isRecurringEvent).map(adaptCalendarEvent)
    const adaptedRecurringEvents = calendarEvents.filter((e) => e.isRecurringEvent).map(adaptRecurringEvent)
    return {
      events: [...adaptedCalenderEvents, ...taskEvents, ...todoEvents],
      recurringEvents: adaptedRecurringEvents,
      entitiesById: merge(keyBy(calendarEvents, "id"), keyBy(tasks, "id"), keyBy(todos, "id")),
    }
  }, [calendarEvents, todos, tasks])

  if (isLoading) return <CircularProgress />
  if (!project) return <ErrorList errors={[translations.projectNotFoundError]} />
  if (isError) return <ErrorList errors={queryErrors} />

  const onAddCalendarEvent = (eventData: NewCalendarEventData) => {
    return createCalendarEvent({
      ...eventData,
      project: project.id,
      organisation: project.isOrgProject ? project.maintainerId : null,
    })
  }

  const onAddTask = async (taskData: Omit<NewTaskData, "projectId">) => {
    const response = project.isOrgProject
      ? await createOrgTask(project.maintainerId, { ...taskData, projectId: project.id })
      : await createUserTask(project.maintainerId, { ...taskData, projectId: project.id })
    if (response) return response.created
  }

  const onAddTodo = (todoData: NewTodoData) => {
    return createTodo({
      ...todoData,
      project: project.id,
      organisation: project.isOrgProject ? project.maintainerId : null,
    })
  }

  return (
    <Calendar
      key={settingsKey} // Force remount calendar when settings change to ensure settings are applied correctly
      events={events}
      recurringEvents={recurringEvents}
      entitiesById={entitiesById}
      canAddCalendarEvents={project.canCreateProjectCalendarEvents}
      canAddTasks={project.canCreateProjectTasks}
      canAddTodos={context?.type !== "supplier" && project.canCreateProjectTodos}
      onAddCalendarEvent={onAddCalendarEvent}
      onAddTask={onAddTask}
      onAddTodo={onAddTodo}
      onUpdateCalendarEvent={updateCalendarEvent}
      onUpdateTask={updateTask}
      onUpdateTodo={updateTodo}
      calendarSettings={calendarSettings}
    />
  )
}

const defaultTranslations = {
  projectNotFoundError: "Project not found",
  fetchErrorCalendarEvents: "Failed to fetch calendar events",
  fetchErrorProject: "Failed to fetch project",
  fetchErrorTasks: "Failed to fetch tasks",
  fetchErrorTodos: "Failed to fetch todos",
  fetchErrorOrganisation: "Failed to fetch organisation",
  fetchErrorHolidays: "Failed to fetch holidays",
  fetchErrorCalendarSettings: "Failed to fetch calendar settings",
}

const useTranslations = (): typeof defaultTranslations => {
  const { t } = useTranslation("translation")

  return {
    projectNotFoundError: t("projectNotFoundError", defaultTranslations.projectNotFoundError),
    fetchErrorCalendarEvents: t("fetchErrorCalendarEvents", defaultTranslations.fetchErrorCalendarEvents),
    fetchErrorProject: t("fetchErrorProject", defaultTranslations.fetchErrorProject),
    fetchErrorTasks: t("fetchErrorTasks", defaultTranslations.fetchErrorTasks),
    fetchErrorTodos: t("fetchErrorTodos", defaultTranslations.fetchErrorTodos),
    fetchErrorOrganisation: t("failedToFetchDataError", defaultTranslations.fetchErrorOrganisation),
    fetchErrorHolidays: t("fetchErrorHolidays", defaultTranslations.fetchErrorHolidays),
    fetchErrorCalendarSettings: t("fetchErrorCalendarSettings", defaultTranslations.fetchErrorCalendarSettings),
  }
}

export default ProjectCalendarRoute
